/**
 * Server lifecycle requests for provisional AI chat exchanges.
 *
 * Also owns the nf_ai_chat request envelope every browser caller shares, so
 * the action, the nonce, and the form-id rule are stated once. The session
 * imports post() from here rather than the reverse: it already depends on
 * this module, and the dependency must not run both ways.
 *
 */
/* global _, define, jQuery, nfAiChat */
define( 'controllers/ai/aiExchangeLifecycle',[], function () {
	'use strict';

	/**
	 * Read the form's id as a number.
	 *
	 * wp_localize_script serializes every value to a string, so an unsaved form
	 * arrives as "0" — which is truthy, and silently defeated the guard that
	 * publishes before the first message. The request then went out with no
	 * usable id and the server answered "A form is required."
	 *
	 * @return {number} Form id, or 0 while the form is unsaved.
	 */
	const currentFormId = function () {
		return 'undefined' === typeof nfAiChat ? 0 : Number( nfAiChat.formId ) || 0;
	};

	/**
	 * Send one nf_ai_chat request.
	 *
	 * Every request the browser makes carries the same envelope — the action,
	 * the nonce, and the form the conversation is filed against — and differs
	 * only in the keys naming the operation. Building it in one place keeps a
	 * renamed key or a changed form-id rule from having to be found again at
	 * each call site. Callers pass only their distinguishing keys, which win
	 * over the envelope if they repeat one.
	 *
	 * @param {Object} payload Keys that distinguish this request.
	 * @return {Object} Pending JSON request.
	 */
	const post = function ( payload ) {
		return jQuery.post(
			nfAiChat.ajaxUrl,
			_.extend(
				{
					action: 'nf_ai_chat',
					security: nfAiChat.nonce,
					form_id: currentFormId(),
				},
				payload
			),
			null,
			'json'
		);
	};

	/**
	 * Send a lifecycle transition for one opaque exchange token.
	 *
	 * @param {string} operation accept_exchange|discard_exchange|revert_exchange.
	 * @param {string} token     Opaque server-issued exchange token.
	 * @return {Object} Pending JSON request.
	 */
	const transition = function ( operation, token ) {
		const payload = {};
		payload[ operation ] = token;
		return post( payload );
	};

	/**
	 * Confirm that a lifecycle response completed the requested transition.
	 *
	 * @param {Object} response WordPress AJAX response.
	 * @param {string} property Expected response data property.
	 * @return {boolean} Whether the transition completed.
	 */
	const completed = function ( response, property ) {
		return !! (
			response &&
			response.data &&
			true === response.data[ property ]
		);
	};

	/**
	 * Accept a provisional exchange after its browser result is verified.
	 *
	 * @param {string} token Opaque server-issued exchange token.
	 * @return {Object} Pending JSON request.
	 */
	const accept = function ( token ) {
		return transition( 'accept_exchange', token );
	};

	/**
	 * Discard a provisional exchange that was not applied.
	 *
	 * @param {string} token Opaque server-issued exchange token.
	 * @return {Object} Pending JSON request.
	 */
	const discard = function ( token ) {
		return transition( 'discard_exchange', token );
	};

	/**
	 * Record the successful browser reversion of an accepted exchange.
	 *
	 * @param {string} token Opaque server-issued exchange token.
	 * @return {Object} Pending JSON request.
	 */
	const revert = function ( token ) {
		return transition( 'revert_exchange', token );
	};

	/**
	 * Query the durable lifecycle state after an ambiguous response.
	 *
	 * @param {string} token Opaque server-issued exchange token.
	 * @return {Object} Pending JSON request.
	 */
	const status = function ( token ) {
		return transition( 'exchange_status', token );
	};

	return {
		accept,
		discard,
		revert,
		status,
		completed,
		post,
	};
} );

